import React, {createContext, useContext, useState, PropsWithChildren, useEffect} from 'react';
import usePoller from "../hooks/usePoller";
import {getTokensPrice} from "../api/coingecko";
import {BridgeToken, getTokens} from "../api/bridge";

const pricesLocalStorageKey = 'h_portfolio_token_prices'

interface PriceState {
  ethereum: number
  tether: number
  harmony: number
  getTokenPrice: (name: string) => number | null
}

const getInitialState = (): PriceState => {
  let priceState = {
    ethereum: 0,
    tether: 0,
    harmony: 0,
    getTokenPrice: () => 1
  }

  try {
    const rawData = localStorage.getItem(pricesLocalStorageKey)
    if(rawData) {
      const data = JSON.parse(rawData)
      priceState = {
        ...priceState,
        ...data
      }
    }
  } catch (e) {}

  return priceState
}

const initialState = getInitialState()
const PriceContext = createContext(initialState);
const pollingInterval = 30 * 60 * 1000

export const useTokenPrice = () => useContext(PriceContext);

export const PriceProvider : React.FC<PropsWithChildren<unknown>> = ({children}) => {
  const [ethereum, setEthereumPrice] = useState<number>(initialState.ethereum)
  const [tether, setTetherPrice] = useState<number>(initialState.tether)
  const [harmony, setHarmonyPrice] = useState<number>(initialState.harmony)
  const [bridgeTokens, setBridgeTokens] = useState<BridgeToken[]>([])
  const [tokensMap, setTokensMap] = useState<Record<string, string>>({})

  const updateLocalStorageCache = () => {
    try {
      localStorage.setItem(pricesLocalStorageKey, JSON.stringify({
        ethereum,
        tether,
        harmony
      }))
    } catch (e) {}
  }

  useEffect(() => {
    updateLocalStorageCache()
  }, [ethereum, tether, harmony])

  usePoller(async () => {
    try {
      const tokensMap = {
        tether: 'tether',
        ethereum: 'ethereum',
        harmony: 'harmony',
      }
      const currency = 'usd'
      const data = await getTokensPrice(Object.keys(tokensMap), currency)
      if(data) {
        if(data[tokensMap.tether] && data[tokensMap.tether][currency]) {
          const price = data[tokensMap.tether][currency]
          setTetherPrice(price)
        }
        if(data[tokensMap.ethereum] && data[tokensMap.ethereum][currency]) {
          const price = data[tokensMap.ethereum][currency]
          setEthereumPrice(price)
        }
        if(data[tokensMap.harmony] && data[tokensMap.harmony][currency]) {
          const price = data[tokensMap.harmony][currency]
          setHarmonyPrice(price)
        }
      }
    } catch (e) {
      console.error('PriceProvider: cannot get Coingecko rates for Tether & Ethereum:', e)
    }

    try {
      const items = await getTokens()
      setBridgeTokens(items)
      const itemsMap: Record<string, string> = {}
      items.forEach((item) => {
        itemsMap[item.symbol.toLowerCase()] = item.usdPrice
      })
      setTokensMap(itemsMap)
    } catch (e) {
      console.error('PriceProvider: failed to get ERC20 tokens price:', e)
    }
  }, pollingInterval)

  const getTokenPrice = (symbol: string) => {
    if(symbol.toLowerCase() === 'wone') {
      return harmony
    }
    const bridgeToken = bridgeTokens.find(item => item.symbol.toLowerCase() === symbol.toLowerCase())
    if(bridgeToken) {
      return +bridgeToken.usdPrice
    }
    return null
  }

  return <PriceContext.Provider value={{
    ethereum,
    tether,
    harmony,
    getTokenPrice,
    // @ts-ignore
    tokensMap,
    tokensCount: Object.keys(tokensMap).length,
  }}>
    {children}
  </PriceContext.Provider>
};
